import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";
import { authenticateWithPasskey } from "@shop-ledger/ui-components";
import type { MfaChallenge, PlatformApiClient, PlatformOperator, PlatformSession } from "../api";
import { clearPlatformSession, loadPlatformSession, savePlatformSession } from "../lib/storage";
import * as webauthnClient from "../api/platformWebauthnClient";

/**
 * Auth state for the platform-operator realm — deliberately a SEPARATE context/provider
 * from anything `dashboard-admin`/`pos-pwa` use, mirroring §9.1's "platform admins are not
 * rows in the User table ... separate auth realm outside RLS entirely." There is no code
 * path anywhere in this app that reuses a tenant-realm login or token; this context talks
 * only to the injected `PlatformApiClient` (mocked today — see ARCHITECTURE.md).
 *
 * Models the real two-step flow §9.1 calls for ("enforce MFA unconditionally") as two
 * distinct states rather than a single boolean, so `LoginPage`/`MfaPage` can't be
 * skipped or reordered by a caller.
 */
export type PlatformAuthStatus = "unauthenticated" | "awaiting-mfa" | "authenticated";

export interface PlatformAuthContextValue {
  status: PlatformAuthStatus;
  operator: PlatformOperator | null;
  loginPending: boolean;
  loginError: string | null;
  mfaError: string | null;
  /** Demo-only convenience — see `MfaChallenge.demo_code`'s doc comment in `api/types.ts`.
   * Surfaced here so `MfaPage` can render it as an explicit on-screen hint rather than
   * silently auto-filling the code, which would misrepresent MFA as having been "passed"
   * invisibly. */
  demoCodeHint: string | null;
  requestLogin: (email: string, password: string) => Promise<void>;
  /** Passkey login — replaces the PASSWORD step only (§9.1: MFA is still unconditional
   * for this realm). Moves `status` to `"awaiting-mfa"` exactly like `requestLogin` does;
   * only ever called against the real backend (`PLATFORM_API_MODE !== "mock"` — a real
   * WebAuthn ceremony has no honest mock-mode equivalent). */
  loginWithPasskey: () => Promise<void>;
  verifyMfa: (code: string) => Promise<void>;
  cancelMfa: () => void;
  logout: () => Promise<void>;
}

const PlatformAuthContext = createContext<PlatformAuthContextValue | null>(null);

export function PlatformAuthProvider({
  client,
  children,
}: {
  client: PlatformApiClient;
  children: ReactNode;
}) {
  const [session, setSession] = useState<PlatformSession | null>(() => loadPlatformSession());
  const [pendingChallenge, setPendingChallenge] = useState<MfaChallenge | null>(null);
  const [loginPending, setLoginPending] = useState(false);
  const [loginError, setLoginError] = useState<string | null>(null);
  const [mfaError, setMfaError] = useState<string | null>(null);

  const requestLogin = useCallback(
    async (email: string, password: string) => {
      setLoginPending(true);
      setLoginError(null);
      setMfaError(null);
      try {
        const challenge = await client.requestLogin(email, password);
        setPendingChallenge(challenge);
      } catch (error) {
        setLoginError(error instanceof Error ? error.message : "Login failed");
        throw error;
      } finally {
        setLoginPending(false);
      }
    },
    [client],
  );

  const loginWithPasskey = useCallback(async () => {
    setLoginPending(true);
    setLoginError(null);
    setMfaError(null);
    try {
      const { session_id, options } = await webauthnClient.webauthnLoginBegin();
      const credential = await authenticateWithPasskey(options);
      const { challenge_id } = await webauthnClient.webauthnLoginComplete(
        session_id,
        credential as Record<string, unknown>,
      );
      setPendingChallenge({ challenge_id });
    } catch (error) {
      setLoginError(
        error instanceof Error && error.name === "PasskeyCancelledError"
          ? "No passkey was found for this site, or the request was cancelled or timed out. Try signing in with your password instead."
          : error instanceof Error
            ? error.message
            : "Could not sign in with a passkey",
      );
      throw error;
    } finally {
      setLoginPending(false);
    }
  }, []);

  const verifyMfa = useCallback(
    async (code: string) => {
      if (!pendingChallenge) throw new Error("No login in progress");
      setLoginPending(true);
      setMfaError(null);
      try {
        const next = await client.verifyMfa(pendingChallenge.challenge_id, code);
        savePlatformSession(next);
        setSession(next);
        setPendingChallenge(null);
      } catch (error) {
        setMfaError(error instanceof Error ? error.message : "Verification failed");
        throw error;
      } finally {
        setLoginPending(false);
      }
    },
    [client, pendingChallenge],
  );

  const cancelMfa = useCallback(() => {
    setPendingChallenge(null);
    setMfaError(null);
  }, []);

  const logout = useCallback(async () => {
    // Re-read from storage rather than trusting this closure's `session` state: the real
    // client (`realPlatformApiClient.ts`) can silently rotate the access/refresh token pair
    // via its own internal `ensureFreshToken()` without this context ever re-rendering with
    // the new tokens (this app's `PlatformApiClient` methods take no token parameter, so
    // there's no natural point to push a rotated pair back up into React state — see that
    // file's top doc comment). Logging out with a stale, already-rotated-away refresh token
    // would still clear the LOCAL session, but would fail to revoke the real (rotated)
    // refresh token server-side, leaving it valid until natural expiry.
    const latest = loadPlatformSession() ?? session;
    if (latest) await client.logout(latest);
    clearPlatformSession();
    setSession(null);
    setPendingChallenge(null);
  }, [client, session]);

  const value = useMemo<PlatformAuthContextValue>(() => {
    const status: PlatformAuthStatus = session ? "authenticated" : pendingChallenge ? "awaiting-mfa" : "unauthenticated";
    return {
      status,
      operator: session?.operator ?? null,
      loginPending,
      loginError,
      mfaError,
      demoCodeHint: pendingChallenge?.demo_code ?? null,
      requestLogin,
      loginWithPasskey,
      verifyMfa,
      cancelMfa,
      logout,
    };
  }, [
    session,
    pendingChallenge,
    loginPending,
    loginError,
    mfaError,
    requestLogin,
    loginWithPasskey,
    verifyMfa,
    cancelMfa,
    logout,
  ]);

  return <PlatformAuthContext.Provider value={value}>{children}</PlatformAuthContext.Provider>;
}

export function usePlatformAuth(): PlatformAuthContextValue {
  const ctx = useContext(PlatformAuthContext);
  if (!ctx) throw new Error("usePlatformAuth() must be used within a <PlatformAuthProvider>");
  return ctx;
}
